import React, { useState } from "react";
import Hero from "../../components/Hero";
import Footer from "../../components/Footer";
import FmdGoodOutlinedIcon from "@mui/icons-material/FmdGoodOutlined";
import MailOutlinedIcon from "@mui/icons-material/MailOutlined";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import CustomButton from "../../components/CustomButton";
import emailjs from "emailjs-com";
import { motion, Variants, Easing } from "framer-motion";
import { ToastContainer, toast } from "react-toastify";

const SERVICE_ID = process.env.REACT_APP_SERVICE_ID || "";
const TEMPLATE_ID = process.env.REACT_APP_TEMPLATE_ID || ""; // main template for admin / notification
const USER_ID = process.env.REACT_APP_USER_ID || ""; // EmailJS public key / user id
const TEMPLATE_ID_CONTACT_US =
  process.env.REACT_APP_TEMPLATE_ID_CONTACT_US || "";

const Contact = () => {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    subject: "",
    message: "",
  });
  const [sending, setSending] = useState(false);

  const ease: Easing = [0.6, -0.05, 0.01, 0.99];

  const containerVariants: Variants = {
    hidden: {},
    visible: { transition: { staggerChildren: 0.15 } },
  };
  const itemVariants: Variants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease } },
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const validate = () => {
    const errors: string[] = [];
    if (!formData.firstName) errors.push("First Name is required");
    if (!formData.lastName) errors.push("Last Name is required");
    if (!formData.email) errors.push("Email is required");
    if (!formData.subject) errors.push("Subject is required");
    if (!formData.message) errors.push("Message is required");

    if (errors.length) {
      errors.forEach((err) => toast.error(err));
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (sending) return;

    if (!SERVICE_ID || !TEMPLATE_ID || !TEMPLATE_ID_CONTACT_US || !USER_ID) {
      console.error("EmailJS configuration missing:", {
        SERVICE_ID,
        TEMPLATE_ID,
        TEMPLATE_ID_CONTACT_US,
        USER_ID,
      });
      toast.error(
        "Email service is not configured properly. Please try again later.",
      );
      return;
    }

    console.log(SERVICE_ID, TEMPLATE_ID, USER_ID);

    if (!validate()) return;

    setSending(true);

    const templateParams = {
      firstName: formData.firstName,
      lastName: formData.lastName,
      email: formData.email,
      subject: formData.subject,
      message: formData.message,
      companyName: "ECOCYNTEC",
      companyAddress:
        "1603 Capitol Ave Ste 415, Cheyenne, Wyoming 82001-4562, USA",
      year: new Date().getFullYear(),
    };

    try {
      //  Send notification to company (Contact Us template)
      await emailjs.send(
        SERVICE_ID,
        TEMPLATE_ID_CONTACT_US,
        templateParams,
        USER_ID,
      );

      // Send auto-reply to visitor
      await emailjs.send(SERVICE_ID, TEMPLATE_ID, templateParams, USER_ID);

      toast.success("Message sent successfully");
      setFormData({
        firstName: "",
        lastName: "",
        email: "",
        subject: "",
        message: "",
      });
    } catch (err) {
      toast.error("An error occure, please try again later.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="w-full ">
      <Hero
        title="Contact Us"
        description={`Reach out to us today and let's start a conversation about how we can collaborate and assist you with your energy needs.`}
        src={require("../../assets/hero1.png")}
      />

      <motion.div
        className="w-full gap-5 flex px-4 md:px-6 my-5 md:my-10"
        variants={containerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.3 }}
      >
        {/* Address panel */}
        <motion.div
          className="w-1/2 hidden lg:flex flex-col items-start justify-start p-10 gap-10 bg-primaryColor"
          variants={itemVariants}
        >
          <h2 className="font-CrimsonText font-bold text-4xl text-white">
            Address
          </h2>
          <div className="w-full flex gap-5">
            <FmdGoodOutlinedIcon fontSize="medium" style={{ color: "white" }} />

            <div>
              <h4 className="font-CrimsonText font-bold text-white text-base md:text-xl">
                Head Office
              </h4>
              <p className="font-CrimsonText text-white text-sm md:text-base">
                1603 Capitol Ave Ste 415
                <br />
                PMB 605565
                <br />
                Cheyenne, Wyoming 82001-4562, USA
              </p>
              <br />
            </div>
          </div>
          <div className="w-full flex gap-5">
            <PhoneOutlinedIcon fontSize="medium" style={{ color: "white" }} />
            <a
              href="tel:+16196934732"
              className="font-CrimsonText text-base text-white hover:underline block"
            >
              +1 (619) 693-4732
            </a>
          </div>
          <div className="w-full flex gap-5">
            <MailOutlinedIcon fontSize="medium" style={{ color: "white" }} />
            <div className="flex flex-col">
              {/* <a
                href="mailto:info@ecocyntec.com"
                className="font-CrimsonText text-base text-white hover:underline block"
              >
                info@ecocyntec.com
              </a>
              <a
                href="mailto:inquiries@ecocyntec.com"
                className="font-CrimsonText text-base text-white hover:underline block"
              >
                inquiries@ecocyntec.com
              </a> */}

              <a
                href="mailto:ops@ecocyntec.com"
                className="font-CrimsonText text-base text-white hover:underline block"
              >
                ops@ecocyntec.com
              </a>
            </div>
          </div>
        </motion.div>

        {/* Form panel */}
        <motion.div
          className="w-full lg:w-1/2 flex-col py-10 bg-white"
          variants={itemVariants}
        >
          <motion.div
            className="w-full flex items-center justify-center gap-5 mb-5 flex-col"
            variants={containerVariants}
          >
            <motion.h2
              className="font-CrimsonText font-bold text-4xl text-primaryColor"
              variants={itemVariants}
            >
              Contact Us
            </motion.h2>

            <motion.div className="w-full flex gap-5" variants={itemVariants}>
              <input
                name="firstName"
                placeholder="First Name"
                value={formData.firstName}
                onChange={handleChange}
                className="w-1/2 h-12 text-textColor pl-4 font-CrimsonText font-semibold text-base md:text-lg placeholder:text-textColor bg-[#DBD8D8] rounded-md outline-none"
              />
              <input
                name="lastName"
                placeholder="Last Name"
                value={formData.lastName}
                onChange={handleChange}
                className="w-1/2 h-12 text-textColor pl-4 font-CrimsonText font-semibold text-base md:text-lg placeholder:text-textColor bg-[#DBD8D8] rounded-md outline-none"
              />
            </motion.div>

            <input
              name="email"
              placeholder="Email"
              value={formData.email}
              onChange={handleChange}
              className="w-full h-12 text-textColor pl-4 font-CrimsonText font-semibold text-base md:text-lg placeholder:text-textColor bg-[#DBD8D8] rounded-md outline-none my-2"
            />
            <input
              name="subject"
              placeholder="Subject"
              value={formData.subject}
              onChange={handleChange}
              className="w-full h-12 text-textColor pl-4 font-CrimsonText font-semibold text-base md:text-lg placeholder:text-textColor bg-[#DBD8D8] rounded-md outline-none my-2"
            />
            <textarea
              name="message"
              placeholder="Message"
              value={formData.message}
              onChange={handleChange}
              className="w-full h-28 text-textColor pl-4 pt-4 font-CrimsonText font-semibold text-base md:text-lg placeholder:text-textColor bg-[#DBD8D8] rounded-md outline-none my-2"
            />
          </motion.div>

          <motion.div variants={itemVariants}>
            <CustomButton
              title="Submit"
              className="hover:opacity-80 cursor-pointer"
              onClick={handleSubmit}
              disabled={sending}
            />
          </motion.div>
        </motion.div>
      </motion.div>

      <Footer />

      {/* Toast container */}
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        pauseOnHover
      />
    </div>
  );
};

export default Contact;
